import React, { useState, useEffect } from "react";
import { Form, notification } from "antd";
import type { FormInstance } from "antd";
import useSWR from "swr";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { ItemClassDataType as AddItemClassDataType } from "../pages/inquiry/add_item_class";
import { ModelDataType as AddModelDataType } from "../pages/inquiry/add_model";
import { useAuth } from "../context/AuthContext";

interface ItemClassType {
     code: string;
     description: string;
}

interface ModelDataType {
     id: number;
     code: string;
     description: string;
}

interface ItemBrandType {
     code: string;
     description: string;
}

interface WarehouseGroupType {
     warehouse_code: string;
     warehouse_name: string;
}

interface SparePartMasterDetailType {
     location_code_rack: string | null;
     id: number;
     status: number;
     brand: string;
     model: string;
     item_code: string;
     item_code1: string | null;
     item_code2: string | null;
     item_name: string;
     item_class: string;
     uom: string | null;
     uom_rate: number | null;
     remark: string | null;
}

const fetcher = (url: string) => axios.get(url).then((res) => res.data);

interface WithSparePartsLogicProps {
     generatedIdSubstitution: string | null;
     form: FormInstance;
     itemClassOptions: { value: string; label: string }[];
     itemBrandOptions: { value: string; label: string }[];
     statusOptions: { value: number; label: string }[];
     isAddModelModalVisible: boolean;
     showAddModelModal: () => void;
     handleAddModelModalCancel: () => void;
     handleModelSelect: (selectedModel: AddModelDataType) => void;
     selectedModelDescription: string | null;
     handleSaveOrUpdate: () => void;
     isAddItemClassModalVisible: boolean;
     showAddItemClassModal: () => void;
     handleAddItemClassModalCancel: () => void;
     isUpdateMode: boolean;
     handleItemClassSelect: (selectedClass: AddItemClassDataType) => void;
     selectedItemClassDescription: string | null;
     selectedLocationRackCode: string | null;
     handleNew: () => void;
     handleAddToStock: () => void;
     isLocationRackModalVisible: boolean;
     showLocationRackModal: () => void;
     handleLocationRackModalCancel: () => void;
     handleLocationRackSelect: (selectedRackCode: string) => void;
     isStockDuplicate: boolean;
}

export const withSparePartsTableLogic = <P extends object>(WrappedComponent: React.ComponentType<P>) => {
     const ComponentWithLogic: React.FC<Omit<P, keyof WithSparePartsLogicProps>> = (props) => {
          const [form] = Form.useForm();
          const { userData } = useAuth();
          const [itemClasses, setItemClasses] = useState<ItemClassType[]>([]);
          const [itemBrands, setItemBrands] = useState<ItemBrandType[]>([]);
          const [isAddItemClassModalVisible, setIsAddItemClassModalVisible] = useState(false);
          const [selectedItemClassDescription, setSelectedItemClassDescription] = useState<string | null>(null);
          const [isAddModelModalVisible, setIsAddModelModalVisible] = useState(false);
          const [selectedModelDescription, setSelectedModelDescription] = useState<string | null>(null);
          const [selectedLocationRackCode, setSelectedLocationRackCode] = useState<string | null>(null);
          const [isLocationRackModalVisible, setIsLocationRackModalVisible] = useState(false);
          const { id: paramId } = useParams<{ id?: string }>();
          const navigate = useNavigate();
          const [isUpdateMode, setIsUpdateMode] = useState<boolean>(false);
          const [updateItemId, setUpdateItemId] = useState<number | null>(null);
          const [originalItemCode, setOriginalItemCode] = useState<string | null>(null);
          const [isStockDuplicate, setIsStockDuplicate] = useState<boolean>(false);
          const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
          const [isProcessing, setIsProcessing] = useState<boolean>(false);

          const handleNew = async () => {
               setIsProcessing(true);
               form.resetFields();
               if (userData?.warehouse_code) {
                    form.setFieldsValue({ warehouse: userData.warehouse_code });
               }
               setSelectedItemClassDescription(null);
               setSelectedModelDescription(null);
               setSelectedLocationRackCode(null);
               setIsUpdateMode(false);
               setUpdateItemId(null);
               setIsStockDuplicate(false);
               notification.destroy();
               notification.info({ message: "Info", description: "Form Clear Succes !", duration: 3 });
               navigate("/dashboard/spareparts_master");
               setIsProcessing(false);
          };

          const showAddItemClassModal = () => {
               setIsAddItemClassModalVisible(true);
          };

          const handleAddItemClassModalCancel = () => {
               setIsAddItemClassModalVisible(false);
          };

          const handleItemClassSelect = (selectedClass: AddItemClassDataType) => {
               console.log("Item Class selected:", selectedClass);
               form.setFieldsValue({ item_class: selectedClass.code });
               setSelectedItemClassDescription(selectedClass.description);
               setIsAddItemClassModalVisible(false);
          };

          const showAddModelModal = () => {
               setIsAddModelModalVisible(true);
          };

          const handleAddModelModalCancel = () => {
               setIsAddModelModalVisible(false);
          };

          const handleModelSelect = (selectedModel: AddModelDataType) => {
               console.log("Model selected:", selectedModel);
               form.setFieldsValue({ model: selectedModel.code });
               setSelectedModelDescription(selectedModel.name);
               handleAddModelModalCancel();
          };

          const showLocationRackModal = () => {
               setIsLocationRackModalVisible(true);
          };

          const handleLocationRackModalCancel = () => {
               setIsLocationRackModalVisible(false);
          };

          const handleLocationRackSelect = (selectedRackCode: string) => {
               form.setFieldsValue({ location_code_rack: selectedRackCode });
               setSelectedLocationRackCode(selectedRackCode);
               setIsLocationRackModalVisible(false);
          };

          const statusOptions = [
               { value: 1, label: "Active" },
               { value: 2, label: "Deactive" },
          ];

          const { data: itemBrandData, error: itemBrandError } = useSWR<ItemBrandType[]>(
               `${process.env.REACT_APP_BACKEND_URL}/api/spareparts/item-brands`,
               fetcher,
          );

          useEffect(() => {
               if (itemBrandError) {
                    console.error("[HOC useEffect] Gagal mengambil data Item Brands:", itemBrandError);
                    setItemBrands([]);
               } else if (itemBrandData) {
                    setItemBrands(itemBrandData);
               }
          }, [itemBrandData, itemBrandError]);

          const {
               data: itemClassData,
               error: itemClassError,
               mutate: mutateItemClasses,
          } = useSWR<ItemClassType[]>(`${process.env.REACT_APP_BACKEND_URL}/api/spareparts/item-classes`, fetcher);

          useEffect(() => {
               if (itemClassError) {
                    console.error("[HOC useEffect] Gagal mengambil data Item Class:", itemClassError);
                    setItemClasses([]);
               } else if (itemClassData) {
                    setItemClasses(itemClassData);
               }
          }, [itemClassData, itemClassError]);

          const itemClassOptions = [
               { value: "", label: "--  Empty --" },
               ...itemClasses.map((item) => ({ value: item.code, label: item.description })),
          ];

          const itemBrandOptions = [
               { value: "", label: "--  Empty --" },
               ...itemBrands.map((item) => ({ value: item.code, label: item.description })),
          ];

          const { data: modelsData, error: modelsError } = useSWR<ModelDataType[]>(
               `${process.env.REACT_APP_BACKEND_URL}/api/spareparts/models`,
               fetcher,
          );

          useEffect(() => {
               if (modelsError) {
                    console.error("[HOC useEffect] Gagal mengambil data Models:", modelsError);
               }
          }, [modelsError]);

          useEffect(() => {
               const currentClassCode = form.getFieldValue("item_class");
               if (currentClassCode && itemClasses.length > 0) {
                    const foundClass = itemClasses.find((cls) => cls.code === currentClassCode);
                    if (foundClass && foundClass.description !== selectedItemClassDescription) {
                         setSelectedItemClassDescription(foundClass.description);
                    }
               }
          }, [itemClasses, form, selectedItemClassDescription]);

          useEffect(() => {
               const currentModelCode = form.getFieldValue("model");
               if (currentModelCode && modelsData && modelsData.length > 0) {
                    const foundModel = modelsData.find((mod) => mod.code === currentModelCode);
                    if (foundModel && foundModel.description !== selectedModelDescription) {
                         setSelectedModelDescription(foundModel.description);
                    }
               }
          }, [modelsData, form, selectedModelDescription]);

          useEffect(() => {
               const fetchDetails = async (id: number) => {
                    setIsLoadingData(true);
                    notification.info({ message: "Loading", description: "Memuat data detail...", key: "loadDetail", duration: 3 });
                    try {
                         const warehouseCode = userData?.warehouse_code || "";
                         const masterRes = await axios.get<SparePartMasterDetailType>(
                              `${process.env.REACT_APP_BACKEND_URL}/api/spareparts/master-detail/${id}?warehouse_code=${warehouseCode}`,
                         );
                         const masterData = masterRes.data;
                         setOriginalItemCode(masterData.item_code);
                         form.setFieldsValue({
                              ...masterData,
                              location_code_rack: masterData.location_code_rack,
                              status: masterData.status ? Number(masterData.status) : undefined,
                         });
                         setSelectedLocationRackCode(masterData.location_code_rack || null);
                         const itemClassCode = masterData.item_class;
                         if (itemClassCode && itemClasses.length > 0) {
                              const foundClass = itemClasses.find((cls) => cls.code === itemClassCode);
                              setSelectedItemClassDescription(foundClass ? foundClass.description : itemClassCode);
                         } else {
                              setSelectedItemClassDescription(null);
                         }

                         const modelCode = masterData.model;
                         if (modelCode) {
                              if (modelsData && modelsData.length > 0) {
                                   const foundModel = modelsData.find((mod) => mod.code === modelCode);
                                   setSelectedModelDescription(foundModel ? foundModel.description : modelCode);
                              } else {
                                   setSelectedModelDescription(modelCode);
                              }
                              form.setFieldsValue({ model: modelCode });
                         } else {
                              setSelectedModelDescription(null);
                              form.setFieldsValue({ model: null });
                         }

                         const checkRes = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/spareparts/check-duplicate`, {
                              item_code: masterData.item_code,
                              warehouse_code: userData?.warehouse_code
                         });
                         setIsStockDuplicate(checkRes.data.inStock);

                         notification.success({ message: "Sukses", description: "Data detail dimuat.", key: "loadDetail", duration: 3 });
                    } catch (error) {
                         console.error("Error fetching item details:", error);
                         notification.error({ message: "Error", description: "Gagal memuat data detail. Kembali ke daftar.", key: "loadDetail", duration: 0 });
                         navigate("/dashboard/browser_data_spareparts");
                    } finally {
                         setIsLoadingData(false);
                    }
               };
               if (paramId) {
                    const numericId = parseInt(paramId, 10);
                    if (!isNaN(numericId)) {
                         setIsUpdateMode(true);
                         setUpdateItemId(numericId);
                         fetchDetails(numericId);
                    } else {
                         notification.error({ message: "Error", description: "ID Item tidak valid.", duration: 0 });
                         navigate("/dashboard/browser_data_spareparts");
                    }
               } else {
                    setIsUpdateMode(false);
                    setUpdateItemId(null);
                    setSelectedItemClassDescription(null);
                    setSelectedModelDescription(null);
               }
          }, [paramId, form, navigate]);

          useEffect(() => {
               const currentItemClass = form.getFieldValue("item_class");
               if (currentItemClass && itemClasses.length > 0) {
                    const found = itemClasses.find((c) => c.code === currentItemClass);
                    if (found) setSelectedItemClassDescription(found.description);
               }

               const currentModel = form.getFieldValue("model");
               if (currentModel && modelsData && modelsData.length > 0) {
                    const found = modelsData.find((m) => m.code === currentModel);
                    if (found) setSelectedModelDescription(found.description);
               }
          }, [itemClasses, modelsData, form]);

          useEffect(() => {
               if (!isUpdateMode && userData?.warehouse_code) {
                    form.setFieldsValue({ warehouse: userData.warehouse_code });
               }
          }, [userData, isUpdateMode, form]);

          const handleSaveOrUpdate = async () => {
               const saveMasterKey = "saveMaster";
               setIsProcessing(true);

               try {
                    const values = await form.validateFields();
                    if (!isUpdateMode) {
                         const checkResponse = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/spareparts/check-duplicate`, { item_code: values.item_code, warehouse_code: values.warehouse });

                         if (checkResponse.data.inMaster) {
                              let dbStatus = "{db_sp_master}";
                              if (checkResponse.data.inStock) dbStatus += " dan {db_sp_stocks}";
                              notification.error({
                                   message: "Gagal menambahkan !",
                                   description: `Item Part sudah ada di data ${dbStatus}`,
                                   placement: "topRight",
                              });
                              return;
                         }
                    }
                    const apiMethod = isUpdateMode ? axios.put : axios.post;
                    const apiUrl = isUpdateMode
                         ? `${process.env.REACT_APP_BACKEND_URL}/api/spareparts/${updateItemId}`
                         : `${process.env.REACT_APP_BACKEND_URL}/api/spareparts/save`;

                    notification.info({
                         message: "Loading",
                         description: isUpdateMode ? "Memperbarui data..." : "Menyimpan data master...",
                         key: saveMasterKey,
                         duration: 3,
                    });
                    const dataToSave: any = {
                         status: values.status,
                         brand: values.brand,
                         model: values.model,
                         item_code: values.item_code,
                         item_code1: values.item_code1,
                         item_code2: values.item_code2,
                         item_name: values.item_name,
                         item_class: values.item_class,
                         uom: values.uom,
                         uom_rate: values.uom_rate,
                         remark: values.remark,
                         location_code_rack: values.location_code_rack,
                         warehouse_code: values.warehouse,
                         original_item_code: originalItemCode,
                    };

                    const responseMaster = await apiMethod(apiUrl, dataToSave);
                    if (responseMaster.status === 201 || responseMaster.status === 200) {
                         notification.success({
                              message: "Sukses",
                              description: `Data master berhasil ${isUpdateMode ? "diperbarui" : "disimpan"}!`,
                              key: saveMasterKey,
                              duration: 3,
                         });
                         form.resetFields();
                         setSelectedItemClassDescription(null);
                         setSelectedModelDescription(null);
                         if (isUpdateMode) {
                              navigate("/dashboard/browser_data_spareparts");
                         } else {
                         }
                    } else {
                         notification.error({
                              message: "Error",
                              description: `Gagal ${isUpdateMode ? "memperbarui" : "menyimpan"} data master: Status ${responseMaster.status}`,
                              key: saveMasterKey,
                              duration: 0,
                         });
                    }
               } catch (error: any) {
                    console.error("Error during save/update process:", error);
                    notification.destroy(saveMasterKey);

                    if (error.errorFields) {
                         notification.error({
                              message: "Error",
                              description: `Gagal ${isUpdateMode ? "memperbarui" : "menyimpan"}. Harap periksa kembali isian form!`,
                              duration: 0,
                         });
                    } else if (axios.isAxiosError(error)) {
                         const errorMsg = error.response?.data?.error || error.message || "Terjadi kesalahan pada server.";
                         notification.error({
                              message: "Error",
                              description: `Gagal ${isUpdateMode ? "memperbarui" : "menyimpan"} data master: ${errorMsg}`,
                              duration: 0,
                         });
                    } else {
                         notification.error({
                              message: "Error",
                              description: `Gagal ${isUpdateMode ? "memperbarui" : "menyimpan"} data. Terjadi kesalahan tidak diketahui.`,
                              duration: 0,
                         });
                    }
               } finally {
                    setIsProcessing(false);
               }
          };

          const handleAddToStock = async () => {
               setIsProcessing(true);
               try {
                    const values = await form.validateFields(['item_code', 'location_code_rack', 'warehouse']);
                    const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/spareparts/add-to-stock`, {
                         item_code: values.item_code,
                         location_code_rack: values.location_code_rack,
                         warehouse_code: values.warehouse
                    });
                    if (response.status === 200 || response.status === 201) {
                         notification.success({
                              message: "Berhasil Menambahkan ke Data !",
                              description: "{db_sp_stocks}",
                              placement: "topRight",
                         });
                    }
               } catch (error: any) {
                    notification.error({ message: "Error", description: error.response?.data?.error || "Gagal menambah ke stock" });
               } finally {
                    setIsProcessing(false);
               }
          };

          return (
               <WrappedComponent
                    {...(props as P)}
                    form={form}
                    itemClassOptions={itemClassOptions}
                    itemBrandOptions={itemBrandOptions}
                    statusOptions={statusOptions}
                    isAddModelModalVisible={isAddModelModalVisible}
                    showAddModelModal={showAddModelModal}
                    handleAddModelModalCancel={handleAddModelModalCancel}
                    handleModelSelect={handleModelSelect}
                    selectedModelDescription={selectedModelDescription}
                    isAddItemClassModalVisible={isAddItemClassModalVisible}
                    showAddItemClassModal={showAddItemClassModal}
                    handleAddItemClassModalCancel={handleAddItemClassModalCancel}
                    selectedItemClassDescription={selectedItemClassDescription}
                    handleItemClassSelect={handleItemClassSelect}
                    isUpdateMode={isUpdateMode}
                    handleNew={handleNew}
                    handleSaveOrUpdate={handleSaveOrUpdate}
                    handleAddToStock={handleAddToStock}
                    selectedLocationRackCode={selectedLocationRackCode}
                    isLocationRackModalVisible={isLocationRackModalVisible}
                    showLocationRackModal={showLocationRackModal}
                    handleLocationRackModalCancel={handleLocationRackModalCancel}
                    handleLocationRackSelect={handleLocationRackSelect}
                    isStockDuplicate={isStockDuplicate}
                    isProcessing={isProcessing} 
               />
          );
     };

     ComponentWithLogic.displayName = `WithSparePartsTableLogic(${WrappedComponent.displayName || WrappedComponent.name || "Component"})`;

     return ComponentWithLogic;
};
